import { useEffect, useState, type FormEvent } from "react";
import { apiFetch } from "../../api/client";

/**
 * Название и описание доски — прямо с доски, не уходя в кабинет.
 *
 * Менять их может только владелец; остальным окно показывает то же самое,
 * но только на чтение: описание нужно и тем, кого позвали по ссылке.
 */
export type BoardInfoDialogProps = {
  boardId: string;
  title: string;
  description: string;
  canEdit: boolean;
  onSaved: (next: { title: string; description: string }) => void;
  onClose: () => void;
};

export function BoardInfoDialog({
  boardId,
  title,
  description,
  canEdit,
  onSaved,
  onClose,
}: BoardInfoDialogProps) {
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftDescription, setDraftDescription] = useState(description);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
      // Горячие клавиши доски не должны срабатывать из поля ввода.
      ev.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!canEdit || busy) return;
    const nextTitle = draftTitle.trim() || "Доска";
    const nextDescription = draftDescription.trim();
    setBusy(true);
    setError("");
    try {
      await apiFetch(`/api/v1/boards/${boardId}`, {
        method: "PATCH",
        json: { title: nextTitle, description: nextDescription },
      });
      onSaved({ title: nextTitle, description: nextDescription });
      onClose();
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : "Не удалось сохранить");
      setBusy(false);
    }
  }

  return (
    <dialog className="wb-modal wb-modal--center" open>
      <div className="wb-modal__box">
        <h2 className="wb-modal__heading">О доске</h2>
        <form onSubmit={onSubmit}>
          <label className="wb-label" htmlFor="board-info-title">
            Название
          </label>
          <input
            id="board-info-title"
            className="wb-input"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            maxLength={255}
            readOnly={!canEdit}
            required
            autoFocus={canEdit}
          />
          <label className="wb-label" htmlFor="board-info-description">
            Описание
          </label>
          <textarea
            id="board-info-description"
            className="wb-input wb-textarea"
            value={draftDescription}
            onChange={(e) => setDraftDescription(e.target.value)}
            maxLength={2000}
            rows={4}
            readOnly={!canEdit}
            placeholder={canEdit ? "Для кого эта доска и что на ней" : "Описания нет"}
          />
          {!canEdit ? (
            <p className="wb-modal__note">
              Менять название и описание может владелец доски.
            </p>
          ) : null}
          {error ? <p className="wb-modal__note">{error}</p> : null}
          <div className="wb-modal__footer">
            <button
              type="button"
              className="wb-modal__btn wb-modal__btn--ghost"
              onClick={onClose}
            >
              {canEdit ? "Отмена" : "Закрыть"}
            </button>
            {canEdit ? (
              <button
                type="submit"
                className="wb-modal__btn wb-modal__btn--primary"
                disabled={busy}
              >
                {busy ? "Сохраняю…" : "Сохранить"}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </dialog>
  );
}
